// src/components/EscalateToSupportModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useFacilityConsole } from '../context/FacilityConsoleContext';
import useNotification from '../hooks/useNotification';
import { createSupportEscalation } from '../services/api';
import { Modal } from './ui/Modal';
import Field from './ui/Field';
import CheckField from './ui/CheckField';

const EscalateToSupportModal = ({ open, onClose, prefillNotes = '' }) => {
  const { selectedFacilityId } = useFacilityConsole();
  const { showNotification } = useNotification();
  const firstInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    fullName: '',
    contactMethod: 'email',
    email: '',
    phone: '',
    notes: prefillNotes
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Focus management
  useEffect(() => {
    if (open && firstInputRef.current) {
      console.debug('[Escalate] Modal opened');
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (formData.contactMethod === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    if (formData.contactMethod === 'phone') {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\+?[\d\s\-\(\)\.]{7,}$/.test(formData.phone.trim())) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    const payload = {
      full_name: formData.fullName.trim(),
      preferred: formData.contactMethod,
      notes: formData.notes.trim(),
      source: 'froggy_chat',
      facility_id: selectedFacilityId
    };

    // Add contact info based on preference
    if (formData.contactMethod === 'email') {
      payload.email = formData.email.trim();
    } else {
      payload.phone = formData.phone.trim();
    }
    
    try {
      const result = await createSupportEscalation(payload);
      
      showNotification('Support request submitted successfully! Someone will contact you shortly.', 'success');
      onClose();
      // Reset form
      setFormData({
        fullName: '',
        contactMethod: 'email',
        email: '',
        phone: '',
        notes: ''
      });
      setErrors({});
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to submit request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // UI leftovers C3 (2026-11-03): a ui/Modal `form` — the hand-made
  // fixed scrim and inline-styled sheet are gone, the words and the
  // request body are the same. `overModal`: it opens from the FroggyAI
  // chat panel (body-level at --z-modal), so it takes the tier above every
  // ordinary dialog — the old sheet sat at an inline 99999 for the same
  // reason. A failed send is the variant's refusal (a red sentence above
  // the buttons); a field's own error sits under that field.
  return (
    <Modal
      isOpen={Boolean(open)}
      onClose={onClose}
      variant="form"
      overModal
      modalId="escalate-support-modal"
      title="Contact Support"
      refusal={errors.submit || null}
      primaryAction={{
        label: 'Send',
        type: 'submit',
        form: 'escalate-support-form',
        disabled: isSubmitting,
        busy: isSubmitting,
        busyLabel: 'Submitting...',
      }}
      onCancel={onClose}
    >
      <p className="ff-text-muted">
        We'll connect you with a human support agent. Please provide your contact information.
      </p>

      <form id="escalate-support-form" className="ff-form" onSubmit={handleSubmit}>
        <Field label="Full Name" required error={errors.fullName || null}>
          <input
            id="fullName"
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            placeholder="Enter your full name"
            required
            disabled={isSubmitting}
            ref={firstInputRef}
          />
        </Field>

        <Field label="Contact Method" required group>
          <CheckField
            type="radio"
            id="contact-email"
            name="contactMethod"
            value="email"
            label="Email"
            checked={formData.contactMethod === 'email'}
            onChange={handleInputChange}
            disabled={isSubmitting}
          />
          <CheckField
            type="radio"
            id="contact-phone"
            name="contactMethod"
            value="phone"
            label="Phone"
            checked={formData.contactMethod === 'phone'}
            onChange={handleInputChange}
            disabled={isSubmitting}
          />
        </Field>

        {formData.contactMethod === 'email' && (
          <Field label="Email Address" required error={errors.email || null}>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your@email.com"
              required
              disabled={isSubmitting}
            />
          </Field>
        )}

        {formData.contactMethod === 'phone' && (
          <Field label="Phone Number" required error={errors.phone || null}>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="(555) 123-4567"
              required
              disabled={isSubmitting}
            />
          </Field>
        )}

        <Field label="Additional Notes">
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Describe your issue or question (optional)"
            disabled={isSubmitting}
            rows={3}
          />
        </Field>
      </form>
    </Modal>
  );
};

export default EscalateToSupportModal;